import { Box, Button, Collapse, useTheme } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";

import Typography from "@mui/material/Typography";

interface ThinkingContentProps {
  text: string;
  isChatConnected: boolean;
}

/**
 * Sanitize thinking content to prevent XSS and unwanted HTML rendering.
 * Uses DOMPurify with no allowed tags to strip all HTML while preserving text content.
 */
const sanitizeThinkingContent = (text: string): string => {
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
};

const ThinkingContent = ({ text, isChatConnected }: ThinkingContentProps) => {
  const theme = useTheme();
  const [checked, setChecked] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const reasoningText = useMemo(() => {
    const stripped = text
      .replace(/^<think>\s*/, "")
      .replace(/\s*<\/think>$/, "");
    // Sanitize HTML to prevent XSS and unwanted HTML rendering
    return sanitizeThinkingContent(stripped);
  }, [text]);

  // Detect if reasoning is actively streaming
  useEffect(() => {
    if (isChatConnected && reasoningText && !text.includes("</think>")) {
      setIsStreaming(true);
    } else {
      setIsStreaming(false);
    }
  }, [text, isChatConnected, reasoningText]);

  const handleChange = () => {
    setChecked((prev) => !prev);
  };

  const showActivityIndicator = isStreaming && !checked;

  // Get the last few words for the streaming preview
  const streamingPreview = useMemo(() => {
    if (!showActivityIndicator || !reasoningText) return "";

    const words = reasoningText.split(/\s+/);
    const lastWords = words.slice(-8).join(" "); // Show last 8 words
    return lastWords.length > 50 ? "..." + lastWords.slice(-47) : lastWords;
  }, [showActivityIndicator, reasoningText]);

  // Brief content preview for the collapsed state (first ~60 chars)
  const contentPreview = useMemo(() => {
    if (!reasoningText.trim()) return "";
    const firstLine = reasoningText.split(/[.!?\n]/)[0]?.trim() || "";
    if (!firstLine) return "";
    return firstLine.length <= 60 ? firstLine : firstLine.slice(0, 57) + "...";
  }, [reasoningText]);

  // Do not render empty think blocks once streaming has finished.
  if (!isStreaming && !reasoningText.trim()) {
    return null;
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Button
        onClick={handleChange}
        variant="text"
        data-attr="assistant:btn:toggle-thinking-output"
        startIcon={checked ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        sx={{
          bgcolor: showActivityIndicator
            ? theme.palette.action.hover
            : theme.palette.grey[200],
          borderRadius: "30px",
          px: 1,
          minWidth: showActivityIndicator ? "200px" : "auto",
          justifyContent: "flex-start",
          textAlign: "left",
        }}
        color="inherit"
        size="small"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.75,
            width: "100%",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: "12px", fontWeight: 500, flexShrink: 0 }}
          >
            {showActivityIndicator ? "Thinking..." : "Thinking output"}
          </Typography>
          {showActivityIndicator && streamingPreview && (
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                color: theme.palette.text.secondary,
                fontStyle: "italic",
                opacity: 0.8,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textTransform: "none",
                animation: "fadeInOut 1.5s ease-in-out infinite",
                "@keyframes fadeInOut": {
                  "0%": { opacity: 0.4 },
                  "50%": { opacity: 0.9 },
                  "100%": { opacity: 0.4 },
                },
              }}
            >
              {streamingPreview}
            </Typography>
          )}
          {!showActivityIndicator && !checked && contentPreview && (
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                color: theme.palette.text.disabled,
                fontStyle: "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textTransform: "none",
              }}
            >
              {contentPreview}
            </Typography>
          )}
        </Box>
      </Button>
      <Box sx={{ display: "flex" }}>
        <Collapse
          in={checked}
          timeout={!checked ? 0 : 100}
          easing={"ease-in-out"}
          sx={{
            borderLeft: `2px solid #d8d8d8`,
            pl: 2,
            mt: checked ? 2 : 0,
            mb: checked ? 2 : 0,
          }}
        >
          <Typography color="text.secondary" sx={{ fontSize: "16px" }}>
            {reasoningText}
          </Typography>
        </Collapse>
      </Box>
    </Box>
  );
};

export default ThinkingContent;
